/**
 * AxiomOrderDebugModal.tsx
 *
 * What Axiom was told about one order — above all, WHERE IT IS GOING.
 *
 * A practice is invoiced for a peptide whether or not it reached the patient,
 * so an order sent to the wrong address is money already spent. Prod order 128
 * is the shape of it: the clinic chose a Rhode Island address, the pharmacy
 * shipped to a stale Florida one off the patient's chart, FedEx delivered it
 * there, and nothing failed — so nobody knew for two days.
 *
 * The addresses therefore come FIRST and as a comparison, not as a JSON dump.
 * The question is never "what is in the payload", it is "is the pharmacy
 * shipping where we said" — so the panel answers that in one line before
 * offering anything else.
 */
import { useQuery } from '@tanstack/react-query';
import { X, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { getAxiomOrderDebug, type DebugAddress } from '@/services/integrations.service';
import { Spinner } from '@/components/ui';
import { getErrorMessage } from '@/services/apiClient';

/** One line, the way an envelope reads. Em dash when there is nothing. */
function formatAddress(a: DebugAddress | null | undefined): string {
  if (!a) return '—';
  const street = [a.line1, a.line2].filter(Boolean).join(', ');
  const rest = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, rest].filter(Boolean).join(' · ') || '—';
}

function AddressRow({ label, address, hint, tone = 'neutral' }: {
  label: string;
  address: DebugAddress | null | undefined;
  hint?: string;
  tone?: 'neutral' | 'good' | 'bad';
}) {
  const toneClass =
    tone === 'bad' ? 'text-red-800' : tone === 'good' ? 'text-emerald-800' : 'text-slate-800';
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-40 flex-shrink-0 text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-sm ${toneClass}`}>
        {formatAddress(address)}
        {hint && <span className="ml-2 text-xs text-slate-500">{hint}</span>}
      </span>
    </div>
  );
}

export function AxiomOrderDebugModal({ patientId, orderId, onClose }: {
  patientId: number;
  orderId: number;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['axiom-order-debug', patientId, orderId],
    // Always refetched: the whole point is what the vendor holds RIGHT NOW, and
    // a cached answer to that question is a wrong answer.
    queryFn: () => getAxiomOrderDebug(patientId, orderId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const ship = data?.shipTo;
  const match = ship?.vendorMatchesChosen;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">What Axiom was sent</h3>
            {data && (
              <p className="mt-0.5 text-xs text-slate-500">
                {data.partnerOrderId}
                {data.externalRef ? ` · Axiom #${data.externalRef}` : ' · not sent yet'}
                {data.fulfillmentStatus ? ` · ${data.fulfillmentStatus}` : ''}
              </p>
            )}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 hover:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}

        {error && (
          <p className="px-5 py-6 text-sm text-red-700">{getErrorMessage(error)}</p>
        )}

        {data && (
          <div className="space-y-5 px-5 py-4">
            {/* The headline: does the pharmacy have the address we chose. */}
            <section>
              {match === false && (
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                  <p className="text-sm text-red-900">
                    <strong>The pharmacy is not shipping to the address chosen on this order.</strong>{' '}
                    The clinic is invoiced either way — check this before it ships.
                  </p>
                </div>
              )}
              {match === true && (
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <p className="text-sm text-emerald-900">The pharmacy has the address chosen on this order.</p>
                </div>
              )}
              {match === null && (
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <Clock className="h-4 w-4 flex-shrink-0 text-slate-500" />
                  <p className="text-sm text-slate-700">Not sent to the pharmacy yet.</p>
                </div>
              )}

              <AddressRow label="Chosen on the order" address={ship?.chosenOnOrder} />
              <AddressRow
                label="At the pharmacy"
                address={ship?.atVendor}
                tone={match === false ? 'bad' : match === true ? 'good' : 'neutral'}
                hint={data.vendorError ?? undefined}
              />
              <AddressRow
                label="Patient chart"
                address={ship?.patientChart}
                hint="where the patient lives on file — not necessarily where orders ship"
              />
            </section>

            {/* What would go out if it were sent now. Explicitly a preview: for
                an order already sent this is today's code and today's data, not
                a record of what actually went. */}
            <section className="rounded-lg border border-slate-200 p-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                If it were sent now
              </h4>
              <p className="mt-1 text-xs text-slate-500">
                A preview built from the order as it stands — not a record of what was sent.
              </p>
              {data.outbound?.error ? (
                <p className="mt-2 text-sm text-red-700">{data.outbound.error}</p>
              ) : (
                <>
                  <AddressRow
                    label="Would ship to"
                    address={data.outbound?.shipTo}
                    hint={
                      data.outbound?.shipToSource === 'chart'
                        ? 'from the patient chart — no address chosen on the order'
                        : data.outbound?.shipToSource === 'order'
                          ? 'the address chosen on the order'
                          : undefined
                    }
                  />
                  {data.outbound?.shipToWarning && (
                    <p className="text-sm text-amber-800">{data.outbound.shipToWarning}</p>
                  )}
                  {!!data.outbound?.items?.length && (
                    <ul className="mt-2 space-y-1">
                      {data.outbound.items.map((it, i) => (
                        <li key={i} className="text-sm text-slate-700">
                          {it.name ?? '—'}
                          {it.quantity && it.quantity > 1 ? ` ×${it.quantity}` : ''}
                          {it.sku && <span className="ml-2 text-xs text-slate-400">{it.sku}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </section>

            {/* The vendor's own record, for anything the summary above omits. */}
            {data.atVendor && (
              <details className="rounded-lg border border-slate-200 p-3">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Axiom's full record
                </summary>
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs text-slate-700">
                  {JSON.stringify({ order: data.atVendor, events: data.vendorEvents }, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
